import { motion, AnimatePresence } from "framer-motion";
import { PenLine } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { KINDS } from "@/lib/simranLive";
import { SIMRAN } from "@/lib/aiot";

const label = (kind, i, L) => {
  const part = KINDS.slice(0, i + 1).filter((k) => k === kind).length;
  return { intro: L("Intro", "परिचय"), explain: L(`Part ${part}`, `भाग ${part}`), check: L(`Check ${part}`, `चेक ${part}`), summary: L("Summary", "सारांश") }[kind];
};

export const Steps = ({ n, done }) => {
  const { L } = useLang();
  return (
    <ol className="flex gap-1.5 overflow-x-auto" data-testid="live-steps">
      {KINDS.map((k, i) => (
        <li key={i} data-testid={`live-step-${i}`} data-state={done || i < n ? "done" : i === n ? "now" : "next"}
          className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold transition-colors ${done || i < n ? "bg-[#14B8A6]/20 text-[#5EEAD4]" : i === n ? "bg-white text-[#0B1F3A]" : "bg-white/5 text-slate-400"}`}>{label(k, i, L)}</li>
      ))}
    </ol>
  );
};

export const Portrait = ({ speaking, writing }) => {
  const { L } = useLang();
  return (
    <div className="relative" data-testid="live-portrait">
      <div className={`absolute -inset-1.5 rounded-[1.6rem] transition-opacity ${speaking ? "opacity-100" : "opacity-0"} bg-gradient-to-br from-[#14B8A6] to-[#5EEAD4] blur-md`} />
      <img src={SIMRAN[1]} alt="Simran Ma'am" className="relative rounded-[1.4rem] w-full aspect-[4/5] object-cover object-[50%_22%]" />
      <div className="absolute left-3 right-3 bottom-3 rounded-xl bg-[#0B1F3A]/85 backdrop-blur px-3 py-2 flex items-center justify-between">
        <span className="text-sm font-extrabold text-white">Simran Ma&apos;am</span>
        <span className="text-[11px] font-semibold text-[#5EEAD4]" data-testid="live-status">
          {writing ? <span className="inline-flex items-center gap-1"><PenLine className="w-3 h-3 animate-pulse" />{L("writing…", "लिख रही हैं…")}</span>
            : speaking ? <span className="inline-flex items-end gap-0.5 h-3">{[0, 1, 2, 3].map((i) => <span key={i} className="w-1 rounded-full bg-[#5EEAD4] animate-bounce" style={{ height: `${6 + i * 2}px`, animationDelay: `${i * 0.12}s` }} />)}</span>
              : L("listening", "सुन रही हैं")}
        </span>
      </div>
    </div>
  );
};

export const Caption = ({ cap, t }) => {
  const m = cap.marks || [];
  const cur = m.reduce((c, x, i) => (t >= x[0] ? i : c), 0);
  return (
    <AnimatePresence mode="wait">
      <motion.p key={cap.text.slice(0, 40)} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
        className="text-lg sm:text-xl leading-relaxed text-slate-300" data-testid="live-caption">
        {m.length ? m.map((x, i) => <span key={i} className={`transition-colors ${i === cur ? "text-white" : i < cur ? "text-slate-400" : ""}`}>{x[2]} </span>) : cap.text}
      </motion.p>
    </AnimatePresence>
  );
};

export const SectionCard = ({ sec }) => (
  <div data-testid="live-section">
    <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight" data-testid="live-section-title">{sec?.title}</h2>
    {sec?.points?.length > 0 && (
      <ul className="mt-4 grid sm:grid-cols-3 gap-2" data-testid="live-points">
        {sec.points.map((p, i) => (
          <motion.li key={`${i}-${p}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 * i }}
            className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm font-semibold text-[#CCFBF1]">{p}</motion.li>
        ))}
      </ul>
    )}
  </div>
);
